/* GENERATED from src/js/ndog-cart-drawer.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  ndog-cart-drawer.js — Cart Drawer v2 (<ndog-cart-drawer>)
  Feature free-gift, 2026-08-18. Rendered by snippets/ndog-cart-drawer.liquid
  from the header, so it exists on every page.

  What it does
  - opens/closes the drawer (header cart icon, ×, Esc, backdrop), locks page
    scroll, keeps keyboard focus inside and restores it on close
  - add-to-cart WITHOUT page reload for forms marked data-ndog-cart-form
    (the redesign PDP): the submit is caught in the capture phase, so the old
    jQuery `.product-form` handler in all.js.min.js (which would reload the
    page) never runs
  - quantity +/−/input and remove per line via the Cart Ajax API
  - after every change the body is re-rendered from the Section Rendering API
    (section ndog-cart-drawer-items comes back inside the same API response)
  - errors are shown inside the drawer (no alert()), actions are queued so
    fast clicks cannot interleave
  - understands the legacy `show_cart` cookie/localStorage flag that old pages
    (legacy PDP, bundle pages) still set after their reload
  - keeps a small counter badge on the header cart links up to date

  Public mini-API for the free-gift module (ndog-free-gift.js):
    getCart() / ensureCart() → last known cart JSON (fetches once if unknown)
    addItems(items, silent)  → POST /cart/add.js with { items }
    updateLines(upd, silent) → POST /cart/update.js with { updates } (line keys)
    updateLineProperties(chg, silent) → POST /cart/change.js with { id, quantity, properties }
                               (the only endpoint that can write line properties; NOT to be
                               confused with the internal changeLine(row, qty) below)
      silent = store the cart but skip the re-render; the caller batches
      operations and renders only the final state (no box jumping)
    refresh()                → re-fetch the section markup (resync after errors)
    setBusy(bool)            → thin progress line on the body
    setCheckoutLocked(bool)  → checkout button disabled + spinner (gift sync pause)
  Events (bubble from the host):
    ndog:cart:updated  detail { cart, paidSubtotalCents }
    ndog:cart:opened / ndog:cart:closed
    ndog:gift:removed  detail { key }  (customer removed a gift line)
*/
if (!customElements.get('ndog-cart-drawer')) {
  customElements.define(
    'ndog-cart-drawer',
    class NdogCartDrawer extends HTMLElement {
      constructor() {
        super();
        this.queue = Promise.resolve();
        this.cart = null;
        this.returnFocusTo = null;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDrawerClick = this.onDrawerClick.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);
        this.onProductFormSubmit = this.onProductFormSubmit.bind(this);
        this.onRowScroll = this.onRowScroll.bind(this);
      }

      // Grabs elements, wires listeners, applies the initial state (badge, legacy flag)
      connectedCallback() {
        this.panel = this.querySelector('.ndog-cart-drawer__panel');
        this.body = this.querySelector('[data-drawer-body]');
        this.message = this.querySelector('[data-drawer-message]');
        this.subtotal = this.querySelector('[data-drawer-subtotal]');
        this.checkoutButton = this.querySelector('[data-drawer-checkout]');
        this.sectionId = this.dataset.sectionId || 'ndog-cart-drawer-items';
        if (!this.panel || !this.body) return;

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        // capture: true — must run BEFORE the jQuery submit handler bound on the form itself
        document.addEventListener('submit', this.onProductFormSubmit, true);
        this.addEventListener('click', this.onDrawerClick);
        this.addEventListener('change', this.onQuantityChange);
        // capture: true — scroll events do not bubble, but capture catches them
        this.addEventListener('scroll', this.onRowScroll, true);

        this.applyEmptyText();
        this.updateFigures();
        this.readLegacyFlag();
        this.updateScrollHints();
      }

      // Removes all listeners
      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('submit', this.onProductFormSubmit, true);
        this.removeEventListener('click', this.onDrawerClick);
        this.removeEventListener('change', this.onQuantityChange);
        this.removeEventListener('scroll', this.onRowScroll, true);
      }

      // Gift rows ([data-scroll-hint]) fade their cut card while more cards
      // wait to the right — the class drives a CSS mask (mobile only)
      onRowScroll(event) {
        const row = event.target;
        if (row instanceof Element && row.hasAttribute('data-scroll-hint')) this.updateScrollHint(row);
      }

      // is-more = there is still content beyond the right edge of the row
      updateScrollHint(row) {
        row.classList.toggle('is-more', row.scrollWidth - row.clientWidth - row.scrollLeft > 8);
      }

      // Initial state for all hint rows (after every render)
      updateScrollHints() {
        this.querySelectorAll('[data-scroll-hint]').forEach((row) => this.updateScrollHint(row));
      }

      /* ------------------------------------------------------------------ */
      /* Open / close                                                        */
      /* ------------------------------------------------------------------ */

      // Opens the drawer, locks page scroll and moves focus to the close button
      open(trigger) {
        if (this.classList.contains('is-open')) return;
        this.returnFocusTo = trigger instanceof HTMLElement ? trigger : document.activeElement;
        this.classList.add('is-open');
        document.documentElement.classList.add('ndog-cart-open');
        document.body.classList.add('ndog-cart-open');
        // Focus after the slide-in so screen readers announce the dialog in place
        window.setTimeout(() => this.focusInside(), 320);
        this.dispatchEvent(new CustomEvent('ndog:cart:opened', { bubbles: true }));
      }

      // Closes the drawer, unlocks scroll and returns focus to the opener
      close() {
        if (!this.classList.contains('is-open')) return;
        this.classList.remove('is-open');
        document.documentElement.classList.remove('ndog-cart-open');
        document.body.classList.remove('ndog-cart-open');
        const target = this.returnFocusTo;
        this.returnFocusTo = null;
        if (target && typeof target.focus === 'function' && document.contains(target)) target.focus();
        this.dispatchEvent(new CustomEvent('ndog:cart:closed', { bubbles: true }));
      }

      // Puts focus on the close button (fallback: the panel itself)
      focusInside() {
        if (!this.classList.contains('is-open')) return;
        const closeButton = this.querySelector('.ndog-cart-drawer__close');
        (closeButton || this.panel).focus();
      }

      // Returns the focusable elements inside the panel (for the Tab trap)
      getFocusable() {
        const selector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
        return Array.from(this.panel.querySelectorAll(selector)).filter((el) => el.offsetParent !== null);
      }

      // Header cart icon → open (the old jQuery handler no longer finds #sliding-cart)
      onDocumentClick(event) {
        const opener = event.target.closest('.open-sliding-cart');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      }

      // Esc closes; Tab cycles inside the open panel
      onKeydown(event) {
        if (!this.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = this.getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      // Legacy pages reload after add-to-cart and set show_cart — open once, then clear the flag
      readLegacyFlag() {
        const cookieOn = document.cookie.split(';').some((c) => c.trim() === 'show_cart=true');
        let storageOn = false;
        try {
          storageOn = window.localStorage.getItem('show_cart') === 'true';
        } catch (error) {
          storageOn = false;
        }
        if (!cookieOn && !storageOn) return;
        // Same shape the theme's setCookie() writes, so both sides stay in sync.
        // NOTE: this runs before jQuery's DOM-ready (defer script) — jQuery would
        // otherwise consume the flag first and look for the old #sliding-cart.
        document.cookie = 'show_cart=false;path=/';
        try {
          window.localStorage.setItem('show_cart', 'false');
        } catch (error) {
          /* storage blocked — nothing to clear */
        }
        this.open();
      }

      /* ------------------------------------------------------------------ */
      /* Actions inside the drawer                                           */
      /* ------------------------------------------------------------------ */

      // Delegated clicks: close, quantity +/−, remove
      onDrawerClick(event) {
        const closer = event.target.closest('[data-close]');
        if (closer) {
          event.preventDefault();
          this.close();
          return;
        }
        const row = event.target.closest('[data-line-key]');
        if (!row) return;
        const input = row.querySelector('[data-qty-input]');
        const current = input ? parseInt(input.value, 10) || 0 : 1;
        if (event.target.closest('[data-qty-plus]')) {
          this.changeLine(row, current + 1);
        } else if (event.target.closest('[data-qty-minus]')) {
          this.changeLine(row, Math.max(0, current - 1));
        } else if (event.target.closest('[data-remove]')) {
          if (row.dataset.giftKey) {
            this.dispatchEvent(new CustomEvent('ndog:gift:removed', { bubbles: true, detail: { key: row.dataset.giftKey } }));
          }
          this.changeLine(row, 0);
        }
      }

      // Typed quantity: commit on change, ignore garbage
      onQuantityChange(event) {
        const input = event.target.closest('[data-qty-input]');
        if (!input) return;
        const row = input.closest('[data-line-key]');
        const value = parseInt(input.value, 10);
        if (!row || Number.isNaN(value) || value < 0) {
          input.value = input.defaultValue;
          return;
        }
        this.changeLine(row, value);
      }

      // Sets the quantity of one line (0 = remove) and re-renders the body
      changeLine(row, quantity) {
        const lineKey = row.dataset.lineKey;
        row.classList.add('is-busy');
        return this.enqueue(async () => {
          try {
            const cart = await this.request(this.url('cart_change_url'), { id: lineKey, quantity });
            this.applyCart(cart, cart.sections);
          } catch (error) {
            row.classList.remove('is-busy');
            this.showError(error);
          }
        });
      }

      /* ------------------------------------------------------------------ */
      /* Add-to-cart without reload (forms marked data-ndog-cart-form)       */
      /* ------------------------------------------------------------------ */

      // Capture-phase submit: stops the legacy reload handler and adds via Ajax
      onProductFormSubmit(event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ndog-cart-form')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const button = event.submitter || form.querySelector('[type="submit"]');
        this.addFromForm(form, button);
      }

      // Posts the form as-is (keeps Seal's hidden property inputs), then opens the drawer
      addFromForm(form, button) {
        this.setButtonLoading(button, true);
        return this.enqueue(async () => {
          try {
            const formData = new FormData(form);
            const data = await this.request(this.url('cart_add_url'), formData);
            const cart = await this.fetchCart();
            this.applyCart(cart, data.sections);
          } catch (error) {
            this.showError(error);
          } finally {
            this.setButtonLoading(button, false);
            this.open(button);
          }
        });
      }

      // Toggles the loading state on the submit button (same classes ndog-atc-loading used)
      setButtonLoading(button, on) {
        if (!button) return;
        button.classList.toggle('is-loading', on);
        if (on) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      }

      /* ------------------------------------------------------------------ */
      /* Cart API                                                            */
      /* ------------------------------------------------------------------ */

      // Builds the .js endpoint from window.routes (falls back to the default paths)
      url(routeName) {
        const routes = window.routes || {};
        const defaults = { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update', cart_url: '/cart' };
        return (routes[routeName] || defaults[routeName]) + '.js';
      }

      // Serialises cart operations so rapid clicks cannot interleave
      enqueue(task) {
        const run = this.queue.then(task, task);
        this.queue = run.catch(() => {});
        return run;
      }

      // POSTs JSON or FormData to a Cart Ajax endpoint and asks for our section
      // back. withSections = false for SILENT operations (2026-08-26): their
      // response markup is discarded anyway, and every `sections` parameter
      // costs a full server-side render of the drawer (~0.3 s on the dev
      // proxy). A gift swap does one silent update + one painting add, so this
      // saves exactly one wasted render per switch.
      async request(url, payload, withSections = true) {
        const options = { method: 'POST', headers: { Accept: 'application/json' } };
        if (payload instanceof FormData) {
          if (withSections) {
            payload.append('sections', this.sectionId);
            payload.append('sections_url', window.location.pathname);
          }
          options.body = payload;
        } else {
          options.headers['Content-Type'] = 'application/json';
          const extras = withSections ? { sections: this.sectionId, sections_url: window.location.pathname } : {};
          options.body = JSON.stringify(Object.assign({}, payload, extras));
        }
        const response = await fetch(url, options);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(data.description || data.message || 'Cart request failed');
          error.status = response.status;
          throw error;
        }
        return data;
      }

      // GET /cart.js — the full cart (add.js only returns the added lines)
      async fetchCart() {
        const response = await fetch(this.url('cart_url'), { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      }

      // Last known cart JSON (may be null before the first change)
      getCart() {
        return this.cart;
      }

      // Cart JSON, fetched once if not known yet
      async ensureCart() {
        if (!this.cart) this.cart = await this.fetchCart();
        return this.cart;
      }

      // Adds several items in one call — used by the free-gift module (throws on failure).
      // silent: store the cart but skip the re-render — the caller batches several
      // operations and renders only the final state, so the boxes never jump.
      addItems(items, silent) {
        return this.enqueue(async () => {
          const data = await this.request(this.url('cart_add_url'), { items }, !silent);
          const cart = await this.fetchCart();
          if (silent) this.cart = cart;
          else this.applyCart(cart, data.sections);
          return cart;
        });
      }

      // Updates several lines at once ({ lineKey: quantity }) — used by the free-gift
      // module; silent works like in addItems (skip render, caller renders the final state)
      updateLines(updates, silent) {
        return this.enqueue(async () => {
          const cart = await this.request(this.url('cart_update_url'), { updates }, !silent);
          if (silent) this.cart = cart;
          else this.applyCart(cart, cart.sections);
          return cart;
        });
      }

      // Rewrites ONE line by key ({ id: lineKey, quantity, properties }) via
      // /cart/change.js — update.js cannot write line item properties. Used by
      // the free-gift module to stamp the bundle contents text (2026-09-09);
      // silent works like in addItems. NOTE: the name must stay distinct from
      // changeLine(row, quantity) above — a second method of the same name in a
      // class body silently replaces the first (2026-09-09: broke line removal).
      updateLineProperties(change, silent) {
        return this.enqueue(async () => {
          const cart = await this.request(this.url('cart_change_url'), change, !silent);
          if (silent) this.cart = cart;
          else this.applyCart(cart, cart.sections);
          return cart;
        });
      }

      // Best-effort resync: re-fetches the section markup so the DOM matches the
      // cart again — used after a silent operation whose follow-up failed.
      // Timestamp param: section GETs can come back stale from the CDN/preview
      // cache — the POST responses (applyCart) are never affected.
      async refresh() {
        try {
          const response = await fetch(`${window.location.pathname}?sections=${this.sectionId}&_=${Date.now()}`, { headers: { Accept: 'application/json' } });
          const sections = await response.json();
          if (sections && sections[this.sectionId]) this.renderBody(sections[this.sectionId]);
          this.updateFigures();
        } catch (error) {
          console.warn('[ndog-cart-drawer] refresh failed', error);
        }
      }

      /* ------------------------------------------------------------------ */
      /* Rendering                                                           */
      /* ------------------------------------------------------------------ */

      // Stores the cart, swaps the body markup, refreshes figures and notifies listeners
      applyCart(cart, sections) {
        this.cart = cart;
        const html = sections && sections[this.sectionId];
        if (html) this.renderBody(html);
        this.updateFigures();
        this.clearError();
        this.dispatchEvent(
          new CustomEvent('ndog:cart:updated', {
            bubbles: true,
            detail: { cart, paidSubtotalCents: this.readPaidSubtotalCents() },
          })
        );
      }

      // Replaces [data-cart-items] with the freshly rendered section markup
      renderBody(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-items]');
        if (!fresh) return;
        const current = this.body.querySelector('[data-cart-items]');
        if (current) current.replaceWith(fresh);
        else this.body.replaceChildren(fresh);
        this.applyEmptyText();
        this.updateScrollHints();
      }

      // The header's own "cart empty" text wins over the locale fallback in the snippet
      applyEmptyText() {
        const empty = this.body.querySelector('[data-empty]');
        if (empty && this.dataset.emptyText) empty.textContent = this.dataset.emptyText;
      }

      // Subtotal, checkout button state and header badge from the rendered body
      updateFigures() {
        const items = this.body.querySelector('[data-cart-items]');
        const count = items ? parseInt(items.dataset.itemCount, 10) || 0 : 0;
        if (this.subtotal && items) this.subtotal.textContent = items.dataset.subtotal || '';
        if (this.checkoutButton) this.checkoutButton.disabled = count === 0 || !!this.checkoutLocked;
        // Empty cart: the footer (subtotal/checkout) hides and the empty
        // state's upsell card takes the bottom edge (2026-10-24, Pablo)
        this.classList.toggle('is-empty', count === 0);
        this.renderBadge(count);
      }

      // Paid subtotal in cents (free-gift tiers) as rendered by Liquid
      readPaidSubtotalCents() {
        const items = this.body.querySelector('[data-cart-items]');
        return items ? parseInt(items.dataset.paidSubtotalCents, 10) || 0 : 0;
      }

      // Small counter badge on every header cart link
      renderBadge(count) {
        const label = this.dataset.countLabel || '';
        document.querySelectorAll('.open-sliding-cart').forEach((link) => {
          let badge = link.querySelector('.ndog-cart-badge');
          if (count > 0) {
            if (!badge) {
              badge = document.createElement('span');
              badge.className = 'ndog-cart-badge';
              badge.setAttribute('aria-hidden', 'true');
              link.appendChild(badge);
            }
            badge.textContent = String(count);
          } else if (badge) {
            badge.remove();
          }
          if (label) link.setAttribute('aria-label', label.replace('[count]', String(count)));
        });
      }

      // Thin progress line while the free-gift module syncs
      setBusy(on) {
        this.body.classList.toggle('is-loading', !!on);
      }

      // Blocks the checkout while the free-gift module still adds gifts — a
      // click in that pause would leave for the checkout without them
      // (2026-10-27). updateFigures keeps the lock across re-renders.
      setCheckoutLocked(on) {
        this.checkoutLocked = !!on;
        if (!this.checkoutButton) return;
        this.checkoutButton.classList.toggle('is-waiting', this.checkoutLocked);
        if (this.checkoutLocked) this.checkoutButton.setAttribute('aria-busy', 'true');
        else this.checkoutButton.removeAttribute('aria-busy');
        this.updateFigures();
      }

      // Shows an error inside the drawer (Shopify's description, else the generic text)
      showError(error) {
        if (!this.message) return;
        const specific = error && error.status && error.message;
        this.message.textContent = specific || this.dataset.errorText || 'Error';
        this.message.classList.add('is-visible');
      }

      // Hides the error message
      clearError() {
        if (!this.message) return;
        this.message.textContent = '';
        this.message.classList.remove('is-visible');
      }
    }
  );
}
